import React, { useCallback, useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import axios from "axios";
import { API_BASE_URL } from "../utils/adminApi";
import "./Models.css";

/* ================= HELPERS ================= */

const getImageSrc = (value = "") => {
  const src = String(value || "").trim();
  if (!src) return "";
  if (
    src.startsWith("data:") ||
    src.startsWith("http://") ||
    src.startsWith("https://")
  ) {
    return src;
  }
  return `${API_BASE_URL}/${src.replace(/^\/+/, "")}`;
};

const normalize = (value = "") =>
  String(value || "").toLowerCase().replace(/\s+/g, " ").trim();

const normalizeCompact = (value = "") =>
  String(value || "").toLowerCase().replace(/[^a-z0-9]/g, "");

const BRAND_SEO = {
  apple: {
    title: "Apple Mobile Repair in Bangalore | iPhone Service",
    description:
      "Get trusted Apple mobile repair in Bangalore with expert iPhone screen replacement, battery repair, charging port fixing, and doorstep support by Servizy.",
    keywords:
      "Apple mobile repair Bangalore, iPhone repair Bangalore, Apple service center, iPhone screen replacement",
    canonical: "https://servizy.co.in/apple-mobile-repair-in-bangalore",
    robots: "index, follow",
  },
  google: {
    title: "Google Mobile Repair in Bangalore | Pixel Repair",
    description:
      "Professional Google mobile repair in Bangalore for Pixel screen replacement, battery repair, software issues, and expert doorstep service.",
    keywords:
      "Google mobile repair Bangalore, Pixel repair Bangalore, Google phone service, smartphone repair",
    canonical: "https://servizy.co.in/google-mobile-repair-in-bangalore",
    robots: "index, follow",
  },
  motorola: {
    title: "Motorola Mobile Repair in Bangalore | Servizy",
    description:
      "Reliable Motorola mobile repair in Bangalore with expert repair solutions for display damage, battery replacement, and charging issues.",
    keywords:
      "Motorola mobile repair Bangalore, Moto phone repair, mobile repair service, smartphone repair",
    canonical: "https://servizy.co.in/motorola-mobile-repair-in-bangalore",
    robots: "index, follow",
  },
  nokia: {
    title: "Nokia Mobile Repair in Bangalore | Phone Repair",
    description:
      "Get affordable Nokia mobile repair in Bangalore with expert technicians for screen repair, battery replacement, and software troubleshooting.",
    keywords:
      "Nokia mobile repair Bangalore, Nokia phone repair, smartphone service, mobile fixing",
    canonical: "https://servizy.co.in/nokia-mobile-repair-in-bangalore",
    robots: "index, follow",
  },
  nothing: {
    title: "Nothing Mobile Repair in Bangalore | Servizy",
    description:
      "Trusted Nothing mobile repair in Bangalore for display replacement, battery issues, charging problems, and software support.",
    keywords:
      "Nothing mobile repair Bangalore, Nothing phone repair, smartphone repair service, mobile service",
    canonical: "https://servizy.co.in/nothing-mobile-repair-in-bangalore",
    robots: "index, follow",
  },
  oneplus: {
    title: "OnePlus Mobile Repair in Bangalore | Doorstep Service",
    description:
      "Professional OnePlus mobile repair in Bangalore with quick repair support for screen damage, battery replacement, and software issues.",
    keywords:
      "OnePlus mobile repair Bangalore, OnePlus phone repair, smartphone repair Bangalore, mobile fixing",
    canonical: "https://servizy.co.in/oneplus-mobile-repair-in-bangalore",
    robots: "index, follow",
  },
  oppo: {
    title: "Oppo Mobile Repair in Bangalore | Phone Service",
    description:
      "Servizy offers reliable Oppo mobile repair in Bangalore with affordable repair services for all Oppo smartphone models.",
    keywords:
      "Oppo mobile repair Bangalore, Oppo phone repair, mobile service Bangalore, smartphone repair",
    canonical: "https://servizy.co.in/oppo-mobile-repair-in-bangalore",
    robots: "index, follow",
  },
  poco: {
    title: "Poco Mobile Repair in Bangalore | Expert Repair",
    description:
      "Get trusted Poco mobile repair in Bangalore with expert repair services for display replacement, battery repair, and charging issues.",
    keywords:
      "Poco mobile repair Bangalore, Poco phone repair, smartphone service, mobile repair center",
    canonical: "https://servizy.co.in/poco-mobile-repair-in-bangalore",
    robots: "index, follow",
  },
  realme: {
    title: "Realme Mobile Repair in Bangalore | Servizy",
    description:
      "Affordable Realme mobile repair in Bangalore for screen replacement, charging port repair, battery replacement, and software support.",
    keywords:
      "Realme mobile repair Bangalore, Realme phone repair, smartphone repair service, mobile fixing",
    canonical: "https://servizy.co.in/realme-mobile-repair-in-bangalore",
    robots: "index, follow",
  },
  redmi: {
    title: "Redmi Mobile Repair in Bangalore | Phone Repair",
    description:
      "Professional Redmi mobile repair in Bangalore with expert technicians for screen repair, battery replacement, and software troubleshooting.",
    keywords:
      "Redmi mobile repair Bangalore, Redmi phone repair, Xiaomi repair Bangalore, smartphone service",
    canonical: "https://servizy.co.in/redmi-mobile-repair-in-bangalore",
    robots: "index, follow",
  },
  samsung: {
    title: "Samsung Mobile Repair in Bangalore | Servizy",
    description:
      "Trusted Samsung mobile repair in Bangalore with fast repair solutions for display damage, battery issues, and charging problems.",
    keywords:
      "Samsung mobile repair Bangalore, Samsung phone repair, smartphone repair Bangalore, mobile service center",
    canonical: "https://servizy.co.in/samsung-mobile-repair-in-bangalore",
    robots: "index, follow",
  },
  vivo: {
    title: "Vivo Mobile Repair in Bangalore | Phone Service",
    description:
      "Professional Vivo mobile repair in Bangalore for screen replacement, battery issues, charging repair, and software support.",
    keywords:
      "Vivo mobile repair Bangalore, Vivo phone repair, smartphone repair service, mobile fixing",
    canonical: "https://servizy.co.in/vivo-mobile-repair-in-bangalore",
    robots: "index, follow",
  },
  xiaomi: {
    title: "Xiaomi Mobile Repair in Bangalore | Expert Service",
    description:
      "Reliable Xiaomi mobile repair in Bangalore with doorstep support for screen replacement, battery repair, and software issues.",
    keywords:
      "Xiaomi mobile repair Bangalore, Xiaomi phone repair, smartphone repair service, mobile fixing",
    canonical: "https://servizy.co.in/xiaomi-mobile-repair-in-bangalore",
    robots: "index, follow",
  },
  asusrog: {
    title: "Asus ROG Phone Mobile Repair in Bangalore | Servizy",
    description:
      "Professional Asus ROG Phone mobile repair in Bangalore for screen replacement, battery repair, charging issues, and gaming phone support.",
    keywords:
      "Asus ROG Phone repair Bangalore, gaming mobile repair, Asus phone service, smartphone repair",
    canonical: "https://servizy.co.in/asus-rog-phone-mobile-repair-in-bangalore",
    robots: "index, follow",
  },
  blackshark: {
    title: "Black Shark Mobile Repair in Bangalore | Expert Service",
    description:
      "Get reliable Black Shark mobile repair in Bangalore with expert technicians for display replacement, battery issues, and software troubleshooting.",
    keywords:
      "Black Shark mobile repair Bangalore, gaming phone repair, smartphone repair service, mobile fixing",
    canonical: "https://servizy.co.in/black-shark-mobile-repair-in-bangalore",
    robots: "index, follow",
  },
  coolpad: {
    title: "Coolpad Mobile Repair in Bangalore | Phone Repair",
    description:
      "Trusted Coolpad mobile repair in Bangalore with quick repair solutions for screen damage, charging port repair, and battery replacement.",
    keywords:
      "Coolpad mobile repair Bangalore, phone repair Bangalore, smartphone repair service, mobile service",
    canonical: "https://servizy.co.in/coolpad-mobile-repair-in-bangalore",
    robots: "index, follow",
  },
  honor: {
    title: "Honor Mobile Repair in Bangalore | Servizy",
    description:
      "Affordable Honor mobile repair in Bangalore for display replacement, battery repair, charging issues, and software support.",
    keywords:
      "Honor mobile repair Bangalore, Honor phone repair, smartphone repair Bangalore, mobile fixing",
    canonical: "https://servizy.co.in/honor-mobile-repair-in-bangalore",
    robots: "index, follow",
  },
  htc: {
    title: "HTC Mobile Repair in Bangalore | Expert Technicians",
    description:
      "Professional HTC mobile repair in Bangalore with reliable solutions for battery replacement, screen repair, and software troubleshooting.",
    keywords:
      "HTC mobile repair Bangalore, HTC phone repair, smartphone repair service, mobile repair center",
    canonical: "https://servizy.co.in/htc-mobile-repair-in-bangalore",
    robots: "index, follow",
  },
  huawei: {
    title: "Huawei Mobile Repair in Bangalore | Doorstep Service",
    description:
      "Servizy provides trusted Huawei mobile repair in Bangalore with expert repair services for all Huawei smartphone models.",
    keywords:
      "Huawei mobile repair Bangalore, Huawei phone repair, smartphone service, mobile fixing",
    canonical: "https://servizy.co.in/huawei-mobile-repair-in-bangalore",
    robots: "index, follow",
  },
  infinix: {
    title: "Infinix Mobile Repair in Bangalore | Servizy",
    description:
      "Reliable Infinix mobile repair in Bangalore with expert support for display replacement, charging issues, battery problems, and software fixes.",
    keywords:
      "Infinix mobile repair Bangalore, Infinix phone repair, smartphone repair Bangalore, mobile fixing",
    canonical: "https://servizy.co.in/infinix-mobile-repair-in-bangalore",
    robots: "index, follow",
  },
  iqoo: {
    title: "iQOO Mobile Repair in Bangalore | Phone Service",
    description:
      "Get professional iQOO mobile repair in Bangalore with fast support for screen damage, battery replacement, and software issues.",
    keywords:
      "iQOO mobile repair Bangalore, iQOO phone repair, smartphone repair service, mobile fixing",
    canonical: "https://servizy.co.in/iqoo-mobile-repair-in-bangalore",
    robots: "index, follow",
  },
  itel: {
    title: "itel Mobile Repair in Bangalore | Expert Repair",
    description:
      "Trusted itel mobile repair in Bangalore with affordable repair solutions for screen replacement, charging issues, and battery problems.",
    keywords:
      "itel mobile repair Bangalore, itel phone repair, smartphone repair Bangalore, mobile service",
    canonical: "https://servizy.co.in/itel-mobile-repair-in-bangalore",
    robots: "index, follow",
  },
  lava: {
    title: "Lava Mobile Repair in Bangalore | Servizy",
    description:
      "Professional Lava mobile repair in Bangalore for display replacement, battery repair, charging port issues, and software support.",
    keywords:
      "Lava mobile repair Bangalore, Lava phone repair, smartphone service center, mobile fixing",
    canonical: "https://servizy.co.in/lava-mobile-repair-in-bangalore",
    robots: "index, follow",
  },
  leeco: {
    title: "LeEco Mobile Repair in Bangalore | Phone Repair",
    description:
      "Affordable LeEco mobile repair in Bangalore with quick and reliable solutions for all smartphone repair needs.",
    keywords:
      "LeEco mobile repair Bangalore, LeEco phone repair, smartphone repair service, mobile fixing",
    canonical: "https://servizy.co.in/leeco-mobile-repair-in-bangalore",
    robots: "index, follow",
  },
  lenovo: {
    title: "Lenovo Mobile Repair in Bangalore | Servizy",
    description:
      "Reliable Lenovo mobile repair in Bangalore for display replacement, battery repair, charging issues, and software troubleshooting.",
    keywords:
      "Lenovo mobile repair Bangalore, Lenovo phone repair, smartphone repair service, mobile fixing",
    canonical: "https://servizy.co.in/lenovo-mobile-repair-in-bangalore",
    robots: "index, follow",
  },
  meizu: {
    title: "Meizu Mobile Repair in Bangalore | Expert Service",
    description:
      "Get trusted Meizu mobile repair in Bangalore with expert technicians for screen repair, battery replacement, and software support.",
    keywords:
      "Meizu mobile repair Bangalore, Meizu phone repair, smartphone repair Bangalore, mobile service",
    canonical: "https://servizy.co.in/meizu-mobile-repair-in-bangalore",
    robots: "index, follow",
  },
  micromax: {
    title: "Micromax Mobile Repair in Bangalore | Servizy",
    description:
      "Professional Micromax mobile repair in Bangalore with reliable repair solutions for display damage, charging issues, and battery replacement.",
    keywords:
      "Micromax mobile repair Bangalore, Micromax phone repair, smartphone repair service, mobile fixing",
    canonical: "https://servizy.co.in/micromax-mobile-repair-in-bangalore",
    robots: "index, follow",
  },
  sonyxperia: {
    title: "Sony Xperia Mobile Repair in Bangalore | Expert Repair",
    description:
      "Trusted Sony Xperia mobile repair in Bangalore with expert repair services for screen replacement, battery repair, and software issues.",
    keywords:
      "Sony Xperia repair Bangalore, Sony mobile repair, smartphone service center, mobile fixing",
    canonical: "https://servizy.co.in/sony-xperia-mobile-repair-in-bangalore",
    robots: "index, follow",
  },
  zte: {
    title: "ZTE Mobile Repair in Bangalore | Servizy",
    description:
      "Get affordable ZTE mobile repair in Bangalore with expert technicians for display replacement, software troubleshooting, and battery repair.",
    keywords:
      "ZTE mobile repair Bangalore, ZTE phone repair, smartphone service, mobile service center",
    canonical: "https://servizy.co.in/zte-mobile-repair-in-bangalore",
    robots: "index, follow",
  },
};

const getBrandSeoKey = (brand = "") => {
  const key = normalizeCompact(brand);
  if (key === "googlepixel" || key === "pixel") return "google";
  if (key === "oneplus" || key === "onepluse") return "oneplus";
  if (key === "mi") return "xiaomi";
  return key;
};

const getSeoContentForBrand = (brand = "") => {
  const readableBrand = String(brand || "").trim() || "Mobile";

  return {
    title: `${readableBrand} Mobile Repair in Bangalore`,

    paragraphs: [ 
      `Looking for reliable ${readableBrand} Mobile Repair in Bangalore services? At Servizy, we provide professional and affordable repair solutions for all ${readableBrand} smartphone models. Whether your mobile has a damaged screen, battery issue, charging problem, software error, or water damage, our skilled technicians deliver quick and dependable repair services.`,

      `${readableBrand} smartphones are popular for their smooth performance, clean Android experience, and durable design. However, accidental damage, hardware faults, or software issues can affect the device over time. Our experienced repair experts carefully diagnose the problem and provide the best repair solution using quality spare parts and advanced repair tools.`,

      `We offer complete ${readableBrand} mobile repair services including display replacement, battery replacement, motherboard repair, charging port repair, speaker repair, camera replacement, software troubleshooting, and water damage recovery. Our technicians are trained to repair all ${readableBrand} smartphone series with accuracy and care.`,

      `At Servizy ${readableBrand} Repair Service, we understand the importance of your smartphone for daily communication and work. That is why we focus on fast service, transparent pricing, and high-quality repairs to ensure complete customer satisfaction.`,

      `Our ${readableBrand} mobile repair center in Bangalore handles both minor and major mobile issues. If your phone is facing overheating, touch screen issues, network problems, battery draining quickly, auto restart issues, or charging failures, our professionals can fix it efficiently. Every repaired device is thoroughly tested before delivery to ensure proper performance.`,

      `We repair a wide range of ${readableBrand} smartphones including G Series, Edge Series, E Series, Razr foldable phones, and other latest ${readableBrand} devices. Our repair team uses modern equipment and safe repair techniques to provide long-lasting repair solutions.`,

      `Choose Servizy ${readableBrand} Mobile Repair Bangalore for trusted and professional smartphone repair services in Bangalore. Contact us today for fast support and affordable ${readableBrand} mobile repair solutions.`
    ],

    faq: [
      {
        question: `Do you repair all ${readableBrand} mobile models?`,
        answer: `Yes, we repair almost all ${readableBrand} smartphones including G Series, Edge Series, E Series, and Razr foldable devices.`
      },

      {
        question: `How long does ${readableBrand} mobile repair take?`,
        answer: `Most ${readableBrand} mobile repairs are completed on the same day depending on the issue and spare part availability.`
      },

      {
        question: `Do you provide ${readableBrand} screen replacement services?`,
        answer: `Yes, we provide ${readableBrand} display and screen replacement services for cracked or damaged screens.`
      },

      {
        question: `What ${readableBrand} mobile issues do you repair?`,
        answer: `We repair display issues, charging problems, battery faults, motherboard issues, camera problems, software errors, and water damage.`
      },

      {
        question: `Do you use quality spare parts for ${readableBrand} repair?`,
        answer: `Yes, we use high-quality compatible spare parts to ensure reliable and long-lasting performance.`
      },

      {
        question: `Is doorstep ${readableBrand} mobile repair available in Bangalore?`,
        answer: `Please contact Servizy Customer Support to check doorstep repair service availability in your area.`
      }
    ]
  };
};

/* ================= COMPONENT ================= */

const Models = ({ forcedBrand = "" }) => {
  const params = useParams();
  const navigate = useNavigate();
  const routeBrand =
    params.brand || params["brand-mobile-repair-in-bangalore"] || "";
  const decodedBrand = forcedBrand
    ? String(forcedBrand).trim().toLowerCase()
    : String(routeBrand)
      .toLowerCase()
      .replace("-mobile-repair-in-bangalore", "")
      .trim();
  const [models, setModels] = useState([]);
  const [brandName, setBrandName] = useState(decodedBrand);
  const [loading, setLoading] = useState(true);
  const seoContent = getSeoContentForBrand(brandName || decodedBrand);
  const seoKey = getBrandSeoKey(brandName || decodedBrand);
  const brandSeo = BRAND_SEO[seoKey] || {
    title: `${brandName || decodedBrand} Mobile Repair in Bangalore | Servizy`,
    description: `Professional ${brandName || decodedBrand} mobile repair in Bangalore with expert support for screen, battery, charging, and software issues.`,
    keywords: `${brandName || decodedBrand} mobile repair Bangalore, ${brandName || decodedBrand} phone repair, smartphone repair service`,
    canonical: `https://servizy.co.in/${normalize(
      brandName || decodedBrand
    ).replace(/\s+/g, "-")}-mobile-repair-in-bangalore`,
    robots: "index, follow",
  };

  const fetchModels = useCallback(async () => {
    try {
      const res = await axios.get(`${API_BASE_URL}/api/models`);

      const selectedBrandLower = normalize(decodedBrand);
      const selectedBrandCompact = normalizeCompact(decodedBrand);

      const filtered = (Array.isArray(res.data) ? res.data : []).filter(
        (m) => {
          const modelBrandName = normalize(m.brandId?.name || "");
          const modelBrandCompact = normalizeCompact(m.brandId?.name || "");
          return (
            modelBrandName === selectedBrandLower ||
            modelBrandCompact === selectedBrandCompact
          );
        }
      );

      setModels(filtered);

      if (filtered.length > 0) {
        setBrandName(filtered[0].brandId?.name || decodedBrand);
      }
    } catch (error) {
      console.error("Model fetch error:", error);
    } finally {
      setLoading(false);
    }
  }, [decodedBrand]);

  useEffect(() => {
    fetchModels();
  }, [fetchModels]);

  return (
    <div className="model-page-wrapper">
      <Helmet>
        <title>{brandSeo.title}</title>
        <meta name="description" content={brandSeo.description} />
        <meta name="keywords" content={brandSeo.keywords} />
        <meta name="robots" content={brandSeo.robots} />
        <meta name="author" content="Servizy" />
        <meta name="publisher" content="Servizy" />
        <meta property="article:publisher" content="https://servizy.co.in/" />
        <link rel="canonical" href={brandSeo.canonical} />
        <meta property="og:title" content={brandSeo.title} />
        <meta property="og:description" content={brandSeo.description} />
        <meta property="og:url" content={brandSeo.canonical} />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="Servizy" />
        <meta property="og:image" content="https://servizy.co.in/s.png" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={brandSeo.title} />
        <meta name="twitter:description" content={brandSeo.description} />
        <meta name="twitter:image" content="https://servizy.co.in/s.png" />
      </Helmet>

      <div className="model-header">
        <div className="header-nav">
          <button className="back-btn" onClick={() => navigate(-1)}>
            Back
          </button>
        </div>

        <h1>
          Select <span>{brandName}</span> Model
        </h1>
      </div>

      {loading && <p className="no-results">Loading models...</p>}

      {!loading && (
        <>
          <div className="model-grid">
            {models.length === 0 && (
              <p className="no-results">No models found.</p>
            )}

            {models.map((model, index) => (
              <div
                key={model._id}
                className="model-card-premium"
                style={{ "--index": index, "--brand-accent": "#007AFF" }}
                onClick={() => {
                  const bookingPath = `/book/${encodeURIComponent(
                    brandName
                  )}/${encodeURIComponent(model.name)}`;
                  navigate(bookingPath);
                }}
              >
                <div className="model-card-content">
                  {model.image && (
                    <div className="model-visual">
                      <img src={getImageSrc(model.image)} alt={model.name} />
                    </div>
                  )}

                  <div className="model-info">
                    <h3>{model.name}</h3>
                    {/* <button type="button" className="select-btn">
                      Select
                    </button> */}
                  </div>
                </div>
              </div>
            ))}
          </div>

          <section className="brand-seo-section">
            <h2>{seoContent.title}</h2>
            <div className="brand-seo-content">
              {seoContent.paragraphs.map((paragraph, idx) => (
                <p
                  key={`${normalizeCompact(seoContent.title)}-${idx}`}
                  className={idx === 0 ? "seo-lead" : ""}
                >
                  {paragraph}
                </p>
              ))}
            </div>
          </section>
          <section className="faq-section">
            <h2>Frequently Asked Questions (FAQ)</h2>

            {(seoContent.faq || []).map((item, index) => (
              <div key={index} className="faq-item">
                <h3>
                  {index + 1}. {item.question}
                </h3>
                <p>{item.answer}</p>
              </div>
            ))}
          </section>
        </>
      )}
    </div>
  );
};

export default Models;
